import { SCORE_STALE_AFTER_MS } from "../../conditions/score-freshness";
import type { MountainForecast, MountainFrame, MountainSport } from "../types";

// Browser-safe presentation of the server-qualified forecast. Revision and
// stored-score validation stay in transport.ts and never run in this module.
const HOUR = 3_600_000;

export function mountainDate(ms: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Zurich",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(ms);
}
export function mountainTime(ms: number): { date: string; hour: number } {
  return {
    date: mountainDate(ms),
    hour: Number(
      new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Zurich",
        hour: "2-digit",
        hourCycle: "h23",
      }).format(ms),
    ),
  };
}
export function mountainDates(today: string): string[] {
  const n = Date.parse(today + "T00:00:00Z");
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(today) ||
    !Number.isFinite(n) ||
    new Date(n).toISOString().slice(0, 10) !== today
  )
    throw new Error("Invalid forecast date");
  return [-1, 0, 1].map((d) =>
    new Date(n + d * 86400000).toISOString().slice(0, 10),
  );
}
export function frameAt(
  forecast: MountainForecast | null | undefined,
  date: string,
  hour: number,
): MountainFrame | null {
  return (
    forecast?.days
      .find((d) => d.date === date)
      ?.hours.find((h) => h.hour === hour) ?? null
  );
}
export function mountainFrameFresh(
  frame: MountainFrame | null,
  now: number,
  maxAge = SCORE_STALE_AFTER_MS,
): boolean {
  if (!frame || frame.utcMs === null || !frame.fetchedAt) return false;
  const age = now - Date.parse(frame.fetchedAt);
  return (
    Number.isFinite(age) &&
    age >= -60000 &&
    age <= (frame.past ? 48 * HOUR : maxAge)
  );
}
export function mountainScoreAt(
  frame: MountainFrame | null,
  sport: MountainSport,
  now: number,
): { value: number | null; text: string } {
  const point = frame?.scores[sport];
  if (!point || !mountainFrameFresh(frame, now))
    return { value: null, text: "Unavailable" };
  if (point.closed) return { value: null, text: "Closed" };
  if (!point.isDay) return { value: null, text: "After dark" };
  const value = point.score === null ? null : Math.round(point.score * 10);
  return { value, text: value === null ? "Unavailable" : `${value}/100` };
}
export function mountainCurve(
  forecast: MountainForecast,
  date: string,
  sport: MountainSport,
  now: number,
) {
  return (forecast.days.find((d) => d.date === date)?.hours ?? []).flatMap(
    (f) => {
      const p = f.scores[sport];
      if (!p || !mountainFrameFresh(f, now) || (p.score === null && !p.closed))
        return [];
      return [
        {
          hour: f.hour,
          score: p.closed ? 0 : p.score!,
          isDay: p.isDay,
          ...(p.closed ? { st: "closed" as const } : {}),
        },
      ];
    },
  );
}
